import React from 'react';
import { FileWarning, Shield, AlertTriangle, File, Tag, Calendar, ExternalLink } from 'lucide-react';

interface DataLeak {
  id: number;
  type: string;
  source: string;
  fileName: string;
  tags: string[];
  date: string;
  severity: 'high' | 'medium' | 'low';
}

interface DataLeaksProps {
  leaks: DataLeak[];
  totalLeaks: number;
  newLeaks: number;
}

const DataLeaks: React.FC<DataLeaksProps> = ({ leaks, totalLeaks, newLeaks }) => {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'text-red-500 bg-red-500/10';
      case 'medium': return 'text-yellow-500 bg-yellow-500/10';
      case 'low': return 'text-emerald-500 bg-emerald-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  return (
    <div className="bg-[#0F1629] rounded-xl p-6 relative overflow-hidden group">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <FileWarning className="h-5 w-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-white">Data Leaks</h2>
          </div>
          <div className="flex items-center space-x-4">
            {/* <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4 text-emerald-500" />
              <span className="text-sm text-gray-400">Protected</span>
            </div> */}
            {newLeaks > 0 && (
              <div className="px-2 py-1 rounded-full bg-red-500/10 text-red-500 text-xs font-medium">
                {newLeaks} new
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Total Leaks</div>
            <div className="text-2xl font-bold text-white">{totalLeaks}</div>
          </div>
          {/* <div className="bg-gray-800/30 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">New Leaks</div>
            <div className="text-2xl font-bold text-red-500">{newLeaks}</div>
          </div> */}
        </div>

        {/* Leaks List */}
        <div className="space-y-3">
          {leaks.map((leak, index) => (
            <div
              key={leak.id}
              className="bg-gray-800/20 rounded-lg p-4 hover:bg-gray-800/30 transition-colors duration-200 cursor-pointer"
              onClick={() => { window.open(`/data-protection/data-leaks/${leak.id}`, '_blank') }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <File className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-white">{leak.type}</span>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(leak.severity)}`}>
                  {leak.severity?.toUpperCase()}
                </span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-2 text-sm">
                  {leak.source.endsWith('.json') ? (
                    <>
                      <File className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-300">{leak.source.split('/').pop()}</span>
                    </>
                  ) : (
                    <a 
                      href={leak.source} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-gray-300 hover:text-blue-400 transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="h-4 w-4 text-gray-500" />
                      <span>View source</span>
                    </a>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {leak.tags.map((tag, tagIndex) => (
                    <div
                      key={tagIndex}
                      className="flex items-center space-x-1 px-2 py-1 rounded-full bg-gray-800/30 text-xs text-gray-400"
                    >
                      <Tag className="h-3 w-3" />
                      <span>{tag}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">{leak.source}</span>
                  <div className="flex items-center space-x-1 text-gray-500">
                    <Calendar className="h-3 w-3" />
                    <span>{leak.date}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {leaks.length === 0 && (
          <div className="text-center py-8">
            <Shield className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <p className="text-gray-400">No data leaks detected</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataLeaks;